import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, ChevronLeft, X, Link as LinkIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { detalleVariante } from '../lib/detalleProducto';

/**
 * Buscador de productos del catálogo, en dos pasos: producto → variante.
 *
 * Se usa en dos sitios: el formulario público y la ficha de una colaboración en
 * el panel. Por eso se apoya en los tokens de color de la app en vez de fijar
 * colores: encaja igual en las dos pantallas.
 *
 * Va contra los endpoints abiertos de /api/catalogo, así que funciona sin
 * sesión.
 *
 * Admite pegar la URL de un producto: si el texto parece una URL no busca, sino
 * que extrae el id y el opId (o el cId) y vincula esa variante directamente.
 */

const MINIMO = 2;
const ESPERA_MS = 300;

// ===== URL de producto =====

export function pareceUrl(texto) {
  const limpio = String(texto || '').trim();
  return /^https?:\/\//i.test(limpio) || /latiendahome\.com\//i.test(limpio);
}

/**
 * Saca productId y la variante (opId o cId) de una URL de La Tienda HOME:
 *   https://www.latiendahome.com/sofa-...-7392.html?opId=YTozOnt...fQ%3D%3D
 *   https://www.latiendahome.com/sofa-...-26661.html?cId=1088748
 * El opId se toma EN CRUDO: ya viene URL-encoded y volver a codificarlo lo
 * rompería (%3D pasaría a %253D). El cId tiene que ser un número. Si vienen
 * los dos, manda el opId.
 */
export function extraerDeUrl(entrada) {
  const limpio = String(entrada || '').trim();
  if (!pareceUrl(limpio)) return null;

  const conOpId = /[?&]opId=([^&#\s]+)/i.exec(limpio);
  const opId = conOpId ? conOpId[1] : '';
  const conCId = /[?&]cId=([^&#\s]+)/i.exec(limpio);
  const cId = conCId && /^\d{1,12}$/.test(conCId[1]) ? conCId[1] : '';

  const sinQuery = limpio.split('?')[0].replace(/\.html$/i, '');
  const conId = /[-/](\d+)$/.exec(sinQuery);
  const productId = conId ? conId[1] : '';

  if (!productId) return null;
  if (opId) return { productId, opId };
  if (cId) return { productId, cId };
  return null;
}

// ===== Formato =====

export function formatearPrecio(numero) {
  if (numero == null || !Number.isFinite(numero)) return '';
  const [entero, decimales] = Math.abs(numero).toFixed(2).split('.');
  const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${numero < 0 ? '-' : ''}${conMiles},${decimales} €`;
}

function descripcionVariante(v) {
  return [v.medida, v.largo].filter(Boolean).join(' · ');
}

// ===== Componente =====

export default function BuscadorProductos({ onElegir, deshabilitado, textoDeshabilitado }) {
  const [consulta, setConsulta] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [producto, setProducto] = useState(null);
  const [variantes, setVariantes] = useState([]);
  const [cargandoVariantes, setCargandoVariantes] = useState(false);
  const [error, setError] = useState('');

  // Identifica la última búsqueda lanzada: si llega tarde una respuesta
  // anterior, se descarta en vez de pisar la nueva.
  const ultimaBusqueda = useRef(0);

  const urlDetectada = pareceUrl(consulta) ? extraerDeUrl(consulta) : null;
  const esUrlInvalida = pareceUrl(consulta) && !urlDetectada;

  useEffect(() => {
    const texto = consulta.trim();
    if (pareceUrl(texto) || texto.length < MINIMO) {
      setResultados([]);
      setBuscando(false);
      return undefined;
    }

    setBuscando(true);
    const marca = ++ultimaBusqueda.current;
    const temporizador = setTimeout(async () => {
      try {
        const res = await fetch(`/api/catalogo/buscar?q=${encodeURIComponent(texto)}`);
        const cuerpo = await res.json();
        if (marca !== ultimaBusqueda.current) return; // llegó tarde
        setResultados(cuerpo.productos || []);
      } catch {
        if (marca === ultimaBusqueda.current) setResultados([]);
      } finally {
        if (marca === ultimaBusqueda.current) setBuscando(false);
      }
    }, ESPERA_MS);

    return () => clearTimeout(temporizador);
  }, [consulta]);

  function reiniciar() {
    setConsulta('');
    setResultados([]);
    setProducto(null);
    setVariantes([]);
    setError('');
  }

  async function abrirProducto(p) {
    setProducto(p);
    setCargandoVariantes(true);
    setError('');
    try {
      const res = await fetch(`/api/catalogo/${encodeURIComponent(p.productId)}/variantes`);
      const cuerpo = await res.json();
      setVariantes(cuerpo.variantes || []);
    } catch {
      setError('No se pudieron cargar las opciones del producto');
    } finally {
      setCargandoVariantes(false);
    }
  }

  function elegirVariante(v) {
    onElegir({
      productId: producto.productId,
      opId: v.opId,
      nombre: producto.nombre,
      categoria: producto.categoria,
      medida: v.medida,
      largo: v.largo,
      color: v.color,
      tapizado: v.tapizado,
      opciones: v.opciones,
      precio: v.precio,
    });
    reiniciar();
  }

  // Camino por URL: se pide la variante concreta al catálogo.
  async function vincularDesdeUrl() {
    setError('');
    setCargandoVariantes(true);
    try {
      const { productId, opId, cId } = urlDetectada;
      // El opId NO se re-codifica: ya viene codificado en la URL de origen.
      const parametro = opId ? `opId=${opId}` : `cId=${encodeURIComponent(cId)}`;
      const res = await fetch(
        `/api/catalogo/${encodeURIComponent(productId)}/variante?${parametro}`,
      );
      if (!res.ok) {
        const cuerpo = await res.json().catch(() => null);
        throw new Error(cuerpo?.error || 'No se encontró ese producto');
      }
      const { producto: p, variante } = await res.json();
      onElegir({
        url: consulta.trim(),
        productId: p.productId,
        opId: variante.opId,
        nombre: p.nombre,
        categoria: p.categoria,
        medida: variante.medida,
        largo: variante.largo,
        color: variante.color,
        tapizado: variante.tapizado,
        opciones: variante.opciones,
        precio: variante.precio,
      });
      reiniciar();
    } catch (err) {
      setError(err.message);
    } finally {
      setCargandoVariantes(false);
    }
  }

  if (deshabilitado) {
    return (
      <div className="rounded-md border border-dashed border-input px-3 py-4 text-center text-xs text-muted-foreground">
        {textoDeshabilitado}
      </div>
    );
  }

  // ===== Paso 2: variantes =====
  if (producto) {
    return (
      <div className="rounded-md border border-input overflow-hidden">
        <div className="flex items-center gap-2 border-b border-input bg-muted/40 px-3 py-2">
          <button
            type="button"
            onClick={() => setProducto(null)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Volver
          </button>
          <span className="text-xs font-semibold text-foreground truncate">{producto.nombre}</span>
        </div>

        {cargandoVariantes ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Cargando opciones…
          </div>
        ) : (
          <div className="max-h-[40vh] overflow-y-auto bg-card">
            {variantes.map((v) => (
              <button
                key={v.opId + v.medida + v.color}
                type="button"
                onClick={() => elegirVariante(v)}
                className="flex w-full items-center gap-3 border-b border-border bg-card px-3 py-2.5 text-left last:border-b-0 hover:bg-accent"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-foreground truncate">
                    {descripcionVariante(v) || 'Opción única'}
                  </span>
                  {detalleVariante(v) && (
                    <span className="block text-xs text-muted-foreground break-words">
                      {detalleVariante(v)}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-sm font-semibold text-foreground">
                  {formatearPrecio(v.precio)}
                </span>
              </button>
            ))}
            {variantes.length === 0 && (
              <p className="px-3 py-4 text-xs text-muted-foreground">Este producto no tiene opciones.</p>
            )}
          </div>
        )}
        {error && <p className="px-3 py-2 text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  // ===== Paso 1: búsqueda =====
  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          placeholder="Busca un producto o pega su enlace..."
          className="w-full rounded-md border border-input bg-card py-3 pl-9 pr-9 text-sm
                     text-foreground outline-none transition-colors
                     placeholder:text-muted-foreground focus:border-ring"
        />
        {consulta && (
          <button
            type="button"
            onClick={reiniciar}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
            title="Limpiar"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {urlDetectada && (
        <button
          type="button"
          onClick={vincularDesdeUrl}
          disabled={cargandoVariantes}
          className="mt-2 flex w-full items-center gap-2 rounded-md border border-primary
                     bg-accent px-3 py-2.5 text-left text-sm text-primary disabled:opacity-60"
        >
          {cargandoVariantes ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LinkIcon className="h-4 w-4" />
          )}
          Añadir desde el enlace — producto {urlDetectada.productId}
        </button>
      )}

      {esUrlInvalida && (
        <p className="mt-2 text-xs text-destructive">
          El enlace no es válido: debe incluir el identificador del producto y el parámetro opId o cId.
        </p>
      )}

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {!pareceUrl(consulta) && consulta.trim().length >= MINIMO && (
        <div className="mt-2 max-h-[40vh] overflow-y-auto rounded-md border border-input bg-card">
          {buscando ? (
            <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Buscando…
            </div>
          ) : resultados.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">Sin resultados.</p>
          ) : (
            resultados.map((p) => (
              <button
                key={p.productId}
                type="button"
                onClick={() => abrirProducto(p)}
                className={cn(
                  'flex w-full items-center gap-3 border-b border-border bg-card px-3 py-2.5',
                  'text-left last:border-b-0 hover:bg-accent',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground truncate">{p.nombre}</span>
                  {p.medidaDesde && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {p.medidaDesde}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {p.precioMin != null && `desde ${formatearPrecio(p.precioMin)}`}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
